// 真实彩票首页：模块入口、积分余额、可售期号、最近订单、菜单
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Container, Box, Card, CardContent, Typography, Button, Stack,
  Grid, Chip, Avatar, Divider, Alert, CircularProgress,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ListAltIcon from '@mui/icons-material/ListAlt';
import VerifiedIcon from '@mui/icons-material/Verified';
import StarIcon from '@mui/icons-material/Star';
import RlPageHeader from '../../components/real-lottery/RlPageHeader';
import KycDialog from '../../components/real-lottery/KycDialog';
import OrderCard from '../../components/real-lottery/OrderCard';
import { BallDisplay } from '../../components/real-lottery/BallPicker';
import { getCurrentRlUser } from '../../components/real-lottery/useRlUser';
import {
  getRlStatus, getRlPoints, getCurrentPeriod, getKyc,
  listRlOrders, getDrawResult,
} from '../../services/realLotteryApi';

export default function RealLotteryHome() {
  const history = useHistory();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [points, setPoints] = useState(0);
  const [pointsPerBet, setPointsPerBet] = useState(10);
  const [period, setPeriod] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [orders, setOrders] = useState([]);
  const [showKyc, setShowKyc] = useState(false);
  const [lastDraw, setLastDraw] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const u = getCurrentRlUser();
    if (!u) {
      history.replace('/login-v2');
      return;
    }
    setUser(u);
    loadData(u.userId);
  }, []);

  const loadData = async (userId) => {
    setLoading(true);
    try {
      const [statusRes, pointsRes, periodRes, kycRes, ordersRes] = await Promise.all([
        getRlStatus(),
        getRlPoints(userId),
        getCurrentPeriod('ssq'),
        getKyc(userId),
        listRlOrders({ userId, status: 'all', page: 1, pageSize: 5 }),
      ]);
      if (statusRes.success) setPointsPerBet(statusRes.data.pointsPerBet);
      if (pointsRes.success) setPoints(pointsRes.data.points);
      if (periodRes.success) setPeriod(periodRes.data);
      if (kycRes.success) setKyc(kycRes.data);
      if (ordersRes.success) setOrders(ordersRes.data.list);

      // 拉一下最近开奖（前一期）
      if (periodRes.success && periodRes.data.period) {
        try {
          const prev = String(parseInt(periodRes.data.period, 10) - 1);
          const draw = await getDrawResult('ssq', prev);
          if (draw.success && draw.data && draw.data.raw) setLastDraw({ period: prev, ...draw.data.raw });
        } catch (e) { /* 静默 */ }
      }
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const handleStartPick = () => {
    if (!kyc || !kyc.verified) {
      setShowKyc(true);
      return;
    }
    history.push('/real-lottery/pick');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 8, backgroundColor: '#f5f5f7', minHeight: '100vh' }}>
      <RlPageHeader title="积分兑彩" subtitle="看视频赚积分 · 10 分换 1 注双色球" />

      <Container maxWidth="sm" sx={{ pt: 2 }}>
        {/* 顶部横幅卡片 */}
        <Card sx={{
          background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
          color: '#fff', mb: 2, borderRadius: 3,
        }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>我的积分</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{points}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  可兑换约 {Math.floor(points / pointsPerBet)} 注
                </Typography>
              </Box>
              <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.2)' }}>
                <StarIcon sx={{ fontSize: 32 }} />
              </Avatar>
            </Stack>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleStartPick}
              sx={{
                mt: 2, py: 1.3, fontSize: 16, fontWeight: 600,
                backgroundColor: '#fff', color: '#d32f2f',
                '&:hover': { backgroundColor: '#fff8e1' },
              }}
              startIcon={<CasinoIcon />}
            >
              立即选号兑彩
            </Button>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* 实名状态 */}
        {!kyc || !kyc.verified ? (
          <Alert
            severity="warning"
            icon={<VerifiedIcon />}
            action={<Button size="small" onClick={() => setShowKyc(true)}>立即认证</Button>}
            sx={{ mb: 2 }}
          >
            首次购彩需实名认证（年满 18 周岁）
          </Alert>
        ) : (
          <Alert severity="success" icon={<VerifiedIcon />} sx={{ mb: 2 }}>
            实名认证已完成：{kyc.maskedName} · {kyc.maskedIdCard}
          </Alert>
        )}

        {/* 当前可售期号 */}
        {period && (
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="caption" color="text.secondary">当前可售</Typography>
                  <Typography variant="h6">双色球 第 {period.period} 期</Typography>
                  {period.endSaleTime && (
                    <Typography variant="caption" color="error">
                      截止：{period.endSaleTime}
                    </Typography>
                  )}
                </Box>
                <Chip label="可投注" color="success" size="small" />
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* 最近开奖 */}
        {lastDraw && (lastDraw.redBalls || lastDraw.red) && (
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary">
                最近开奖 · 第 {lastDraw.period} 期
              </Typography>
              <Box sx={{ mt: 1 }}>
                <BallDisplay
                  red={(lastDraw.redBalls || lastDraw.red || '').split(',')}
                  blue={(lastDraw.blueBalls || lastDraw.blue || '').split(',')[0] || ''}
                />
              </Box>
            </CardContent>
          </Card>
        )}

        {/* 功能菜单 */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <MenuTile icon={<ListAltIcon />} label="我的彩票"
              onClick={() => history.push('/real-lottery/orders')} />
          </Grid>
          <Grid item xs={4}>
            <MenuTile icon={<EmojiEventsIcon />} label="中奖记录"
              onClick={() => history.push('/real-lottery/orders?status=won')} />
          </Grid>
          <Grid item xs={4}>
            <MenuTile icon={<VerifiedIcon />} label="兑奖记录"
              onClick={() => history.push('/real-lottery/redemptions')} />
          </Grid>
        </Grid>

        {/* 最近订单 */}
        <Box sx={{ mb: 1.5, mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>最近彩票</Typography>
          <Button size="small" onClick={() => history.push('/real-lottery/orders')}>查看全部</Button>
        </Box>
        {orders.length === 0 ? (
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">还没有彩票记录，去选号试试运气吧！</Typography>
            </CardContent>
          </Card>
        ) : (
          orders.map((o) => <OrderCard key={o.outTradeNo} order={o} />)
        )}
      </Container>

      {user && (
        <KycDialog
          open={showKyc}
          userId={user.userId}
          onClose={() => setShowKyc(false)}
          onSuccess={(data) => { setKyc(data); }}
        />
      )}
    </Box>
  );
}

function MenuTile({ icon, label, onClick }) {
  return (
    <Card
      onClick={onClick}
      sx={{
        textAlign: 'center', py: 2, cursor: 'pointer', borderRadius: 2,
        '&:hover': { boxShadow: 3 },
      }}
    >
      <Box sx={{ color: 'primary.main', mb: 0.5 }}>{icon}</Box>
      <Typography variant="caption" sx={{ fontWeight: 500 }}>{label}</Typography>
    </Card>
  );
}
